import Cookies from 'js-cookie';

import { hasConsent } from './cookieConsent';

/**
 * Where a signup came from: the visit's campaign tags (utm_* and Meta's fbclid), kept in this browser and sent
 * with the signup as `acquisition`, so each account records the ad that brought it (meu-treino-api stores an
 * allow-listed copy, private). Last touch: a visit with new tags replaces the old ones; a visit without tags
 * keeps them. Tags older than ATTRIBUTION_DAYS are dropped.
 */
const STORAGE_KEY = 'mt-acquisition';
const TAGS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'fbclid'];
const ATTRIBUTION_DAYS = 30;

const read = () => {
  try {
    const stored = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || 'null');
    if (!stored || typeof stored !== 'object') return null;
    const age = Date.now() - Date.parse(stored.firstSeenAt);
    return age >= 0 && age < ATTRIBUTION_DAYS * 864e5 ? stored : null;
  } catch {
    return null;
  }
};

const write = (value) => {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch {
    // private window or blocked storage: the signup simply goes without tags
  }
};

const externalHost = (referrer, ownHost) => {
  try {
    const { hostname } = new URL(referrer);
    return hostname && hostname !== ownHost ? hostname : undefined;
  } catch {
    return undefined;
  }
};

// On every page load (main.jsx)
export const captureAcquisition = (location = window.location, referrer = document.referrer) => {
  const params = new URLSearchParams(location.search);
  const tags = Object.fromEntries(
    TAGS.map((key) => [key, params.get(key)?.slice(0, 500)]).filter(([, value]) => value)
  );
  if (!Object.keys(tags).length && read()) return;
  const referrerHost = externalHost(referrer, location.hostname);
  if (!Object.keys(tags).length && !referrerHost) return;
  write({
    ...tags,
    // The path alone: the tags are kept apart, and any other parameter of the link stays out
    landingPath: location.pathname.slice(0, 300),
    ...(referrerHost ? { referrerHost } : {}),
    firstSeenAt: new Date().toISOString(),
  });
};

const newEventId = () => {
  try {
    return window.crypto.randomUUID();
  } catch {
    return `${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
  }
};

/**
 * What goes with the signup: the kept tags, whether the visitor accepted cookies (Meta only hears of the signup
 * with consent), the event id shared by the pixel and the API, and Meta's cookies when the pixel set them
 */
export const signupAcquisition = () => {
  const fbp = Cookies.get('_fbp');
  const fbc = Cookies.get('_fbc');
  const adConsent = hasConsent();
  // Meta's click id only serves the Meta event, which needs consent: without it, it stays out of the account
  const { fbclid, ...tags } = read() || {};
  return {
    ...tags,
    ...(adConsent && fbclid ? { fbclid } : {}),
    adConsent,
    eventId: newEventId(),
    ...(fbp ? { fbp } : {}),
    ...(fbc ? { fbc } : {}),
  };
};
